import { z } from 'zod'
import { HASH_STRING_PATTERN } from './version.js'

const Id = z.string().min(1).max(128)
const IsoDateTime = z.string().datetime()
const OptionalId = Id.optional()

/**
 * Persistent workspace contracts — SM2W (B11.2, B15.3, §8.5, §14.1, MCP-AC-106..119).
 *
 * The workspace holds **explicitly published or MCP-created copies**, and they are service-readable
 * by design: no zero-knowledge or end-to-end claim is ever made about them (B14.3). Nothing in this
 * file may be read as "the local vault is uploaded": publication is a deliberate act, and the local
 * original is never touched remotely.
 *
 * Item versions are immutable; index status is per version; a deleted item is tombstoned so a late
 * indexing job can never republish it.
 */

export const WorkspaceProvenance = z.enum(['MCP_CREATED', 'UPLOADED', 'PUBLISHED_COPY'])
export type WorkspaceProvenance = z.infer<typeof WorkspaceProvenance>

/**
 * Unpublish, archive, disconnect and delete are distinct effects (DEC-11):
 * `UNPUBLISHED` keeps the item and its versions with access withdrawn, `ARCHIVED` keeps it readable
 * by the owner but out of the active workspace, `DELETED` removes blobs, text and indexes within the
 * approved bound and leaves a tombstone. `DISCONNECTED` describes a host grant losing access — the
 * item is untouched.
 */
export const WorkspacePublicationState = z.enum([
  'PRIVATE',
  'PUBLISHED',
  'UNPUBLISHED',
  'ARCHIVED',
  'DELETED',
])
export type WorkspacePublicationState = z.infer<typeof WorkspacePublicationState>

/** Honest processing states: a copy whose text could not be extracted says so (MCP-AC-119). */
export const WorkspaceIndexStatus = z.enum([
  'PENDING',
  'INDEXED',
  'TEXT_UNAVAILABLE',
  'OCR_PENDING',
  'OCR_FAILED',
  'FAILED',
])
export type WorkspaceIndexStatus = z.infer<typeof WorkspaceIndexStatus>

export const WorkspaceRetentionPolicy = z.enum(['WORKSPACE_DEFAULT', 'UNTIL_UNPUBLISHED'])
export type WorkspaceRetentionPolicy = z.infer<typeof WorkspaceRetentionPolicy>

export const WorkspaceItemVersion = z
  .object({
    version: z.number().int().positive(),
    content_digest: z.string().regex(HASH_STRING_PATTERN),
    bytes: z.number().int().nonnegative(),
    pages: z.number().int().nonnegative(),
    index_status: WorkspaceIndexStatus,
    /** The index generation this version was processed under, when it was processed. */
    index_version: z.number().int().positive().optional(),
    created_at: IsoDateTime,
  })
  .strict()
export type WorkspaceItemVersion = z.infer<typeof WorkspaceItemVersion>

export const WorkspaceItem = z
  .object({
    id: Id,
    owner_user_id: Id,
    title: z.string().min(1).max(300),
    provenance: WorkspaceProvenance,
    /** The host grant a published copy came from, when it came from one. */
    originating_grant_id: OptionalId,
    /** Local source identity, so a caller can tell a copy from its local original without bytes. */
    source_identity: z.string().min(1).max(200).optional(),
    publication_state: WorkspacePublicationState,
    retention_policy: WorkspaceRetentionPolicy,
    current_version: z.number().int().positive(),
    versions: z.array(WorkspaceItemVersion).min(1),
    created_at: IsoDateTime,
    updated_at: IsoDateTime,
    /** Set when the item was unpublished, archived or deleted; the reason is not content. */
    state_changed_at: IsoDateTime.optional(),
  })
  .strict()
  .superRefine((item, ctx) => {
    if (item.current_version !== Math.max(...item.versions.map((version) => version.version))) {
      ctx.addIssue({
        code: 'custom',
        message: 'current_version must be the newest version in the list',
        path: ['current_version'],
      })
    }
    if (item.publication_state === 'DELETED' && item.state_changed_at === undefined) {
      ctx.addIssue({
        code: 'custom',
        message: 'a deleted item records when it was deleted',
        path: ['state_changed_at'],
      })
    }
  })
export type WorkspaceItem = z.infer<typeof WorkspaceItem>

/* --------------------------------- search --------------------------------- */

/**
 * Search limits are product decisions, not resource calibration: a page is 20 by default and 100 at
 * most, a content chunk is 4,000 characters, and one response never exceeds 16,000 characters
 * (B18.1). Workspace *resource* limits live in the config register and stay unset until measured.
 */
export const WORKSPACE_SEARCH_LIMITS = {
  page_size_default: 20,
  page_size_max: 100,
  content_chunk_chars: 4000,
  response_chars_ceiling: 16000,
} as const

export const WorkspaceSearchRequest = z
  .object({
    query: z.string().min(1).max(500),
    cursor: z.string().min(1).max(512).optional(),
    page_size: z
      .number()
      .int()
      .positive()
      .max(WORKSPACE_SEARCH_LIMITS.page_size_max)
      .default(WORKSPACE_SEARCH_LIMITS.page_size_default),
    /** Restrict to specific items or a specific version; applied before matching and ranking. */
    item_ids: z.array(Id).max(50).optional(),
    version: z.number().int().positive().optional(),
    /** Full text and snippets require `content.read` over a granted item (metadata-only by default). */
    include_content: z.boolean().default(false),
    include_archived: z.boolean().default(false),
  })
  .strict()
export type WorkspaceSearchRequest = z.infer<typeof WorkspaceSearchRequest>

export const WorkspaceSearchResult = z
  .object({
    item_id: Id,
    title: z.string().min(1).max(300),
    version: z.number().int().positive(),
    index_status: WorkspaceIndexStatus,
    /** Present only when content was authorized and available; never a whole document. */
    snippet: z.string().max(WORKSPACE_SEARCH_LIMITS.content_chunk_chars).optional(),
    /** Honest reason when a snippet is absent although content was requested. */
    snippet_unavailable: z
      .enum(['NOT_AUTHORIZED', 'TEXT_UNAVAILABLE', 'OCR_PENDING', 'OCR_FAILED'])
      .optional(),
  })
  .strict()
  .superRefine((result, ctx) => {
    if (result.snippet !== undefined && result.snippet_unavailable !== undefined) {
      ctx.addIssue({
        code: 'custom',
        message: 'a result carries a snippet or a reason, never both',
        path: ['snippet'],
      })
    }
  })
export type WorkspaceSearchResult = z.infer<typeof WorkspaceSearchResult>

export const WorkspaceSearchResponse = z
  .object({
    results: z.array(WorkspaceSearchResult).max(WORKSPACE_SEARCH_LIMITS.page_size_max),
    /**
     * Matching items for this viewer **after** the authorization predicate, before pagination.
     * Optional in the contract so an existing consumer keeps parsing, always populated by the
     * service: a count that ignored authorization would be a disclosure, so it is never computed
     * without one.
     */
    total: z.number().int().nonnegative().optional(),
    next_cursor: z.string().min(1).max(512).nullable(),
    /** Echoed so a caller can prove the authorization filter ran before matching and ranking. */
    applied_filters: z
      .object({
        item_ids: z.array(Id).optional(),
        version: z.number().int().positive().optional(),
        include_archived: z.boolean(),
        content_authorized: z.boolean(),
      })
      .strict(),
    /** True when the response was shortened to stay inside the character ceiling. */
    truncated: z.boolean(),
  })
  .strict()
export type WorkspaceSearchResponse = z.infer<typeof WorkspaceSearchResponse>

/* ------------------------------- operations ------------------------------- */

export const WorkspacePublishRequest = z
  .object({
    client_request_id: z.string().min(1).max(128),
    /** The local document being published; the local original is never modified. */
    local_document_id: Id,
    title: z.string().min(1).max(300),
    content_digest: z.string().regex(HASH_STRING_PATTERN),
    bytes: z.number().int().positive(),
    pages: z.number().int().positive(),
    /** Explicit consent to a service-readable copy; publication is never automatic. */
    confirm_service_readable: z.literal(true),
  })
  .strict()
export type WorkspacePublishRequest = z.infer<typeof WorkspacePublishRequest>

export const WorkspaceCreateRequest = z
  .object({
    client_request_id: z.string().min(1).max(128),
    title: z.string().min(1).max(300),
    content_digest: z.string().regex(HASH_STRING_PATTERN),
    bytes: z.number().int().positive(),
    pages: z.number().int().positive(),
  })
  .strict()
export type WorkspaceCreateRequest = z.infer<typeof WorkspaceCreateRequest>

export const WorkspaceUpdateRequest = z
  .object({
    client_request_id: z.string().min(1).max(128),
    /** The version being replaced; a mismatch is a conflict, not a silent overwrite. */
    expected_version: z.number().int().positive(),
    content_digest: z.string().regex(HASH_STRING_PATTERN),
    bytes: z.number().int().positive(),
    pages: z.number().int().positive(),
  })
  .strict()
export type WorkspaceUpdateRequest = z.infer<typeof WorkspaceUpdateRequest>

export const WorkspaceStateChangeRequest = z
  .object({
    client_request_id: z.string().min(1).max(128),
    reason: z.string().max(200).optional(),
  })
  .strict()
export type WorkspaceStateChangeRequest = z.infer<typeof WorkspaceStateChangeRequest>

export const WorkspaceDeleteResponse = z
  .object({
    item_id: Id,
    publication_state: z.literal('DELETED'),
    /** Purge is bounded and monitored; the deadline is disclosed, never open-ended. */
    purge_within_hours: z.number().int().positive(),
    tombstoned_at: IsoDateTime,
    /**
     * Saved agreement copies the purge deliberately left alone. A retained agreement keeps its
     * final PDF and evidence together under the workspace policy, so deleting the item that
     * happened to hold the bytes removes the item, not the retained agreement (DEC-09, MCP-AC-116).
     */
    retained_agreement_copies: z.number().int().nonnegative().optional(),
  })
  .strict()
export type WorkspaceDeleteResponse = z.infer<typeof WorkspaceDeleteResponse>

/**
 * The answer to a version's byte upload: what the service stored and the digest it computed from
 * the received bytes — never the caller's claim.
 */
export const WorkspaceContentUploadResponse = z
  .object({
    item_id: Id,
    version: z.number().int().positive(),
    bytes: z.number().int().positive(),
    stored_digest: z.string().regex(HASH_STRING_PATTERN),
  })
  .strict()
export type WorkspaceContentUploadResponse = z.infer<typeof WorkspaceContentUploadResponse>

export const WorkspaceItemResponse = z.object({ item: WorkspaceItem }).strict()
export type WorkspaceItemResponse = z.infer<typeof WorkspaceItemResponse>

export const WorkspaceListResponse = z
  .object({
    items: z.array(WorkspaceItem).max(WORKSPACE_SEARCH_LIMITS.page_size_max),
    next_cursor: z.string().min(1).max(512).nullable(),
  })
  .strict()
export type WorkspaceListResponse = z.infer<typeof WorkspaceListResponse>

/**
 * A transaction source is an independent immutable object, so a workspace copy can never be the
 * signing source of an active request (MCP-AC-116).
 */
export const WORKSPACE_INDEPENDENCE_RULES = {
  local_originals_uploaded_automatically: false,
  transaction_sources_are_workspace_objects: false,
  unpublish_removes_blobs: false,
  archive_removes_access: false,
  disconnect_changes_item: false,
  deleted_item_can_be_republished_by_a_late_job: false,
} as const

/* ------------------------------- lifecycle -------------------------------- */

/**
 * The lifecycle state machine (DEC-11). Every transition is listed explicitly so "unpublish",
 * "archive", "disconnect" and "delete" cannot collapse into one another: unpublishing withdraws
 * host access and keeps the item and its versions, archiving keeps it readable by the owner but
 * out of the active workspace, and deleting is the only effect that removes bytes.
 */
export const WORKSPACE_STATE_TRANSITIONS = {
  PRIVATE: ['PUBLISHED', 'ARCHIVED', 'DELETED'],
  PUBLISHED: ['UNPUBLISHED', 'ARCHIVED', 'DELETED'],
  UNPUBLISHED: ['PUBLISHED', 'ARCHIVED', 'DELETED'],
  ARCHIVED: ['PUBLISHED', 'UNPUBLISHED', 'DELETED'],
  DELETED: [],
} as const satisfies Readonly<Record<WorkspacePublicationState, readonly string[]>>

/** What each effect removes. The table is the product decision; no implementation may widen it. */
export const WORKSPACE_EFFECTS = {
  unpublish: { withdraws_host_access: true, removes_blobs: false, removes_item: false },
  archive: { withdraws_host_access: true, removes_blobs: false, removes_item: false },
  disconnect: { withdraws_host_access: true, removes_blobs: false, removes_item: false },
  delete: { withdraws_host_access: true, removes_blobs: true, removes_item: true },
} as const

/**
 * Retention `UNTIL_UNPUBLISHED` means exactly that: unpublishing releases the retained copy's
 * text and index (the item and its version metadata stay, because unpublish is not delete).
 */
export const WORKSPACE_RETENTION_END_STATE: WorkspaceIndexStatus = 'TEXT_UNAVAILABLE'

/* ---------------------------- publication disclosure ---------------------- */

/**
 * Publication is explicit and its disclosure is recorded (MCP-AC-117). The disclosure is a
 * positive statement — a copy the service can read — and it never softens into a
 * confidentiality claim, because workspace copies are service-readable by design (B14.3, §14.1).
 */
export const WorkspacePublicationDisclosure = z
  .object({
    service_readable: z.literal(true),
    disclosure_key: z.literal('workspace.disclosure.service_readable'),
    disclosed_at: IsoDateTime,
    originating_grant_id: OptionalId,
    copied_from_local_document_id: OptionalId,
  })
  .strict()
export type WorkspacePublicationDisclosure = z.infer<typeof WorkspacePublicationDisclosure>

export const WorkspacePublishResponse = z
  .object({ item: WorkspaceItem, disclosure: WorkspacePublicationDisclosure })
  .strict()
export type WorkspacePublishResponse = z.infer<typeof WorkspacePublishResponse>

/**
 * Copy discipline (B14.3): a workspace copy is readable by the service, and no surface may say
 * otherwise. The list is checked against every workspace message key and every workspace string
 * the API can return, so the claim cannot reappear in copy or in code.
 */
export const FORBIDDEN_WORKSPACE_CLAIMS: readonly string[] = [
  'zero-knowledge',
  'zero knowledge',
  'end-to-end encrypted',
  'end to end encrypted',
  'e2ee',
  'we cannot read',
  'we can not read',
  'only you can read',
  'server cannot decrypt',
]

export function forbiddenWorkspaceClaimIn(text: string): string | null {
  const lowered = text.toLowerCase()
  return FORBIDDEN_WORKSPACE_CLAIMS.find((claim) => lowered.includes(claim)) ?? null
}

/* --------------------------- processing gate & states ---------------------- */

/**
 * The processing gate (§11.8, V17-CFG-09). Extraction, OCR and indexing run only inside the
 * bounded processing runtime, and that runtime does not exist until the benchmark in
 * `09-v17-integration-register.md` §3 records measured CPU, memory, queue delay and cost. Until
 * then the gate is closed and nothing is ever extracted: a published copy stays `PENDING` with an
 * honest reason rather than being processed by an unreviewed path.
 */
export const WORKSPACE_PROCESSING_GATE = {
  enabled_by_default: false,
  register_gate_key: 'v17_cfg_09',
  deployment_switch_value: 'AUTHORIZED',
  closed_reason: 'PROCESSING_GATE_CLOSED',
} as const

/** Why a processing claim was refused. Machine codes only — never content. */
export const WORKSPACE_PROCESSING_REFUSALS: readonly string[] = [
  'GATE_CLOSED',
  'LIMITS_UNMEASURED',
  'CONCURRENCY_LIMIT',
  'PAGE_BUDGET',
  'BYTE_BUDGET',
  'TIME_BUDGET',
  'MEMORY_BUDGET',
  'INDEX_BYTES_BUDGET',
  'MALFORMED',
  'ENCRYPTED',
  'UNSUPPORTED_TYPE',
  'RETRIES_EXHAUSTED',
]

/** The `index_error_code` vocabulary stored beside an honest index status. */
export const WORKSPACE_INDEX_ERROR_CODES: readonly string[] = [
  'TEXT_LAYER_ABSENT',
  'OCR_UNAVAILABLE',
  'OCR_FAILED',
  'JOB_PAGE_BUDGET',
  'JOB_PIXEL_BUDGET',
  'JOB_BYTE_BUDGET',
  'JOB_TIME_BUDGET',
  'JOB_MEMORY_BUDGET',
  'INDEX_BYTES_BUDGET',
  'MALFORMED_DOCUMENT',
  'ENCRYPTED_DOCUMENT',
  'RETENTION_ENDED',
  'SUPERSEDED',
  'RETRIES_EXHAUSTED',
]

/** The processing budget a claim carries; every bound comes from the register, never a literal. */
export const WorkspaceProcessingBudget = z
  .object({
    max_pages: z.number().int().positive(),
    /** Input bytes the runtime may account for: the document and everything decoded from it. */
    max_bytes: z.number().int().positive(),
    /**
     * Device pixels of declared page geometry (§11.8). Geometry is what the runtime reads, so the
     * bound is on the pages a document claims — the rasterization workload that would consume
     * pixels is explicitly not supported, and this is the bound that keeps a page-geometry bomb
     * from being walked at all.
     */
    max_pixels: z.number().int().positive(),
    max_time_ms: z.number().int().positive(),
    max_memory_bytes: z.number().int().positive(),
    max_index_bytes: z.number().int().positive(),
    max_attempts: z.number().int().positive(),
  })
  .strict()
export type WorkspaceProcessingBudget = z.infer<typeof WorkspaceProcessingBudget>

/**
 * The processing **profile** — SM2W §11.8, B15.3, V17-CFG-09.
 *
 * A profile is the identity of a deterministic workload: the same profile version produces the same
 * output from the same input, so it is part of every cache key, every job and every benchmark
 * fixture. The supported workload is deliberately narrow — a PDF **text layer** — because a bounded
 * runtime can only promise determinism for the work it actually performs:
 *
 *  * no rasterization and no OCR (an image-only page is reported `OCR_UNAVAILABLE`, never guessed
 *    at), no font-program execution, no JavaScript/action execution, no decryption;
 *  * **no outbound fetch of any kind**: the runtime reads only the bytes of the verified blob row
 *    for the item version it was authorized for, so a document can never make the service retrieve
 *    an arbitrary URL (B15.3);
 *  * content is untrusted data: parsed with bounded loops and fixed-size windows, never evaluated,
 *    never concatenated into SQL, and the extracted text is stored as a value.
 */
export const WORKSPACE_PROCESSING_PROFILE = {
  /** Bump when the extractor's output could change; every cache key and job row carries it. */
  version: 'pdf-text/1',
  supported_media_types: ['application/pdf'],
  /**
   * PDF user units are points (1/72 inch) and the workload declares no raster scale, so geometry is
   * accounted in its own unit: one page point is one device unit. No DPI is claimed anywhere.
   */
  device_units_per_point: 1,
  /**
   * The decompression-bomb guard: a single stream may not inflate beyond this ratio of its stored
   * size. It is a property of the workload (what the profile is willing to decode), not a
   * deployment resource, so it lives here and not in the register — and what it does inflate is
   * still charged to the job's byte budget.
   */
  max_stream_ratio: 200,
  rasterization: false,
  ocr: false,
  outbound_fetch: false,
  executes_document_code: false,
} as const

export type WorkspaceProcessingProfileVersion = typeof WORKSPACE_PROCESSING_PROFILE.version

/** The statuses the runtime may complete a job with (a subset of `WorkspaceIndexStatus`). */
export const WORKSPACE_EXTRACTION_OUTCOMES = [
  'INDEXED',
  'TEXT_UNAVAILABLE',
  'OCR_PENDING',
  'OCR_FAILED',
  'FAILED',
] as const
export type WorkspaceExtractionOutcome = (typeof WORKSPACE_EXTRACTION_OUTCOMES)[number]

/**
 * What one bounded run measured, recorded with the job. It is how "the run was bounded" is checkable
 * after the fact rather than asserted: the accounted peak is what the memory budget compared
 * against, and the output bytes are what the index-bytes budget compared against.
 */
export const WorkspaceExtractionStats = z
  .object({
    profile_version: z.string().min(1).max(64),
    pages: z.number().int().nonnegative(),
    input_bytes: z.number().int().nonnegative(),
    decoded_bytes: z.number().int().nonnegative(),
    output_bytes: z.number().int().nonnegative(),
    /** Peak accounted allocation at any point of the run. */
    peak_accounted_bytes: z.number().int().nonnegative(),
    elapsed_ms: z.number().int().nonnegative(),
    /** The largest decompression ratio a single stream reached (the bomb guard reads this). */
    max_stream_ratio: z.number().nonnegative(),
  })
  .strict()
export type WorkspaceExtractionStats = z.infer<typeof WorkspaceExtractionStats>

/** The digest a version's index entry is valid under: content, retention and index generation. */
export const WorkspaceIndexContext = z
  .object({
    content_digest: z.string().regex(HASH_STRING_PATTERN),
    retention_policy: WorkspaceRetentionPolicy,
    index_version: z.number().int().positive(),
  })
  .strict()
export type WorkspaceIndexContext = z.infer<typeof WorkspaceIndexContext>

/* ------------------------------- monitoring ------------------------------- */

/**
 * Purge and re-index monitoring: a deletion's bound and its overdue state, and how many re-index
 * attempts remain. Reported rather than assumed, so an overdue purge is visible instead of silent.
 */
export const WorkspacePurgeReport = z
  .object({
    item_id: Id,
    purge_due_at: IsoDateTime,
    purged_at: IsoDateTime.nullable(),
    attempts: z.number().int().nonnegative(),
    overdue: z.boolean(),
    last_error_code: z.string().min(1).max(64).nullable(),
  })
  .strict()
export type WorkspacePurgeReport = z.infer<typeof WorkspacePurgeReport>

/* ------------------------------ list & lookup ----------------------------- */

export const WorkspaceListQuery = z
  .object({
    cursor: z.string().min(1).max(512).optional(),
    page_size: z
      .number()
      .int()
      .positive()
      .max(WORKSPACE_SEARCH_LIMITS.page_size_max)
      .default(WORKSPACE_SEARCH_LIMITS.page_size_default),
    /**
     * Archived items stay the owner's; they are simply out of the active workspace. A query string
     * carries text, so the flag is the literal `true` or `false` — an unrecognised value is refused
     * rather than coerced, because a coerced `"false"` would be the dangerous direction.
     */
    include_archived: z.enum(['true', 'false']).default('false'),
  })
  .strict()
export type WorkspaceListQuery = z.infer<typeof WorkspaceListQuery>

/** The boolean a list request means, from the wire flag the contract accepts. */
export function includeArchivedFromQuery(value: WorkspaceListQuery['include_archived']): boolean {
  return value === 'true'
}

/* -------------------------- saved agreement copies ------------------------ */

/**
 * A saved workspace agreement (DEC-09, MCP-AC-114): the final PDF and its evidence retained together
 * under the workspace policy, surviving the transactional TTLs. `signing_authority_extended` is the
 * literal `0` — a retained copy is a record, never a signing authority.
 */
export const WorkspaceAgreementCopy = z
  .object({
    copy_id: Id,
    item_id: Id,
    envelope_id: Id,
    completion_id: Id,
    artifact_object_key: z.string().min(1).max(400),
    artifact_hash: z.string().regex(HASH_STRING_PATTERN),
    evidence_manifest_key: z.string().min(1).max(400),
    evidence_manifest_digest: z.string().regex(HASH_STRING_PATTERN),
    retention_policy: WorkspaceRetentionPolicy,
    transactional_deadline: IsoDateTime.nullable(),
    signing_authority_extended: z.literal(0),
    saved_at: IsoDateTime,
  })
  .strict()
export type WorkspaceAgreementCopy = z.infer<typeof WorkspaceAgreementCopy>

export const WorkspaceAgreementCopyRequest = z
  .object({
    client_request_id: z.string().min(1).max(128),
    envelope_id: Id,
    /**
     * The explicit save preference carried into completion (B11.2). `false` is the transaction-only
     * path: no copy is created and nothing becomes readable (MCP-AC-116).
     */
    save_completed_copy: z.boolean(),
    retention_policy: WorkspaceRetentionPolicy.optional(),
  })
  .strict()
export type WorkspaceAgreementCopyRequest = z.infer<typeof WorkspaceAgreementCopyRequest>

export const WorkspaceAgreementCopyResponse = z
  .object({ saved: z.boolean(), copy: WorkspaceAgreementCopy.nullable() })
  .strict()
export type WorkspaceAgreementCopyResponse = z.infer<typeof WorkspaceAgreementCopyResponse>

export const WorkspaceAgreementListResponse = z
  .object({ copies: z.array(WorkspaceAgreementCopy).max(WORKSPACE_SEARCH_LIMITS.page_size_max) })
  .strict()
export type WorkspaceAgreementListResponse = z.infer<typeof WorkspaceAgreementListResponse>

/** The retained-copy rules, stated once so a surface cannot describe them differently. */
export const WORKSPACE_RETAINED_AGREEMENT_RULES = {
  retains_final_pdf_and_evidence: true,
  survives_transactional_ttl: true,
  extends_signing_authority: false,
} as const
